/**
 * Transfer Plan Page
 * 
 * Main page for creating transfer plans from CSV files.
 */

import * as React from "react";
import { TransferPlanUpload } from "./components/TransferPlanUpload";
import { TransferPlanTable } from "./components/TransferPlanTable";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Download, AlertCircle, CheckCircle2, CheckCircle, List } from "lucide-react";
import { transferPlanService, type TransferPlanResult } from "@/services/transferPlanService";
import { toast } from "sonner";
import { Link } from "wouter";

export function TransferPlanPage() {
  const [result, setResult] = React.useState<TransferPlanResult | null>(null);
  const [isProcessing, setIsProcessing] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [isConfirming, setIsConfirming] = React.useState(false);
  const [isConfirmed, setIsConfirmed] = React.useState(false);

  const handleFileSelect = async (file: File) => {
    setIsProcessing(true);
    setError(null);
    setResult(null);
    setIsConfirmed(false);

    try {
      const transferResult = await transferPlanService.uploadCsvFile(file);
      setResult(transferResult);
      toast.success("CSV file processed successfully", {
        description: `${transferResult.summary.matchedCount} payments matched, ${transferResult.summary.unmatchedCount} unmatched`,
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to process CSV file";
      setError(errorMessage);
      toast.error("Failed to process CSV file", {
        description: errorMessage,
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDownload = async () => {
    if (!result) return;

    try {
      await transferPlanService.downloadTransferPlan(result);
      toast.success("Transfer plan downloaded successfully");
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to download transfer plan";
      toast.error("Failed to download transfer plan", {
        description: errorMessage,
      });
    }
  };

  const handleConfirm = async () => {
    if (!result) return;

    // Show confirmation alert
    const confirmed = window.confirm(
      `Are you sure you want to confirm this transfer plan?\n\n` +
      `This will update ${result.summary.matchedCount} payment requests with batch '${result.batch.batchName}' and income data.`
    );

    if (!confirmed) {
      return;
    }

    setIsConfirming(true);
    setError(null);

    try {
      const confirmResult = await transferPlanService.confirmTransferPlan(result);
      
      if (confirmResult.success) {
        setIsConfirmed(true);
        toast.success("Transfer plan confirmed successfully", {
          description: `Updated ${confirmResult.updatedCount} payment requests`,
        });
      } else {
        throw new Error(confirmResult.message || "Failed to confirm transfer plan");
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to confirm transfer plan";
      setError(errorMessage);
      toast.error("Failed to confirm transfer plan", {
        description: errorMessage,
      });
    } finally {
      setIsConfirming(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  // Income calculation functions (same as in TransferPlanTable)
  const calculateOrpusIncome = (amount: number): number => {
    if (amount === 134000) {
      return 24000;
    } else if (amount === 154000) {
      return 20000;
    } else if (amount === 222000) {
      return 48000;
    } else if (amount === 510000) {
      return 120000;
    } else if (amount === 570000) {
      return 140000;
    } else {
      return 140000;
    }
  };

  const calculateOrdaIncome = (amount: number): number => {
    if (amount === 134000) {
      return 40000;
    } else if (amount === 154000) {
      return 40000;
    } else if (amount === 222000) {
      return 72000;
    } else if (amount === 510000) {
      return 180000;
    } else if (amount === 570000) {
      return 200000;
    } else {
      return 200000;
    }
  };

  // Calculate income totals
  const incomeStats = React.useMemo(() => {
    if (!result) {
      return {
        totalWimaxIncome: 0,
        totalOrpusIncome: 0,
        totalOrdaIncome: 0,
        totalOrlokIncome: 0,
      };
    }

    const totals = result.matchedPayments.reduce(
      (acc, payment) => {
        acc.totalWimaxIncome += 30000 - payment.csvRow.transactionFee;
        acc.totalOrpusIncome += calculateOrpusIncome(payment.csvRow.amount);
        const ordaIncome = calculateOrdaIncome(payment.csvRow.amount);
        acc.totalOrdaIncome += ordaIncome;
        // ORLOK income is always the same as ORDA income
        acc.totalOrlokIncome += ordaIncome;
        return acc;
      },
      {
        totalWimaxIncome: 0,
        totalOrpusIncome: 0,
        totalOrdaIncome: 0,
        totalOrlokIncome: 0,
      }
    );

    return totals;
  }, [result]);

  return (
    <div className="flex-1 space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Transfer Plan</h1>
          <p className="text-muted-foreground">
            Upload a CSV file to create a transfer plan by matching orders with payment requests.
          </p>
        </div>
        <Link href="/keuangan/transfer-plans">
          <Button variant="outline" className="gap-2">
            <List className="h-4 w-4" />
            View All Transfer Plans
          </Button>
        </Link>
      </div>

      <TransferPlanUpload
        onFileSelect={handleFileSelect}
        isProcessing={isProcessing}
        error={error}
      />

      {result && (
        <>
          <Card>
            <CardHeader>
              <CardTitle>Summary</CardTitle>
              <CardDescription>Processing results and statistics</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Total Rows</p>
                  <p className="text-2xl font-bold">{result.summary.totalRows}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Matched</p>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-5 w-5 text-green-600" />
                    <p className="text-2xl font-bold text-green-600">
                      {result.summary.matchedCount}
                    </p>
                  </div>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Unmatched</p>
                  <div className="flex items-center gap-2">
                    <AlertCircle className="h-5 w-5 text-orange-600" />
                    <p className="text-2xl font-bold text-orange-600">
                      {result.summary.unmatchedCount}
                    </p>
                  </div>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Total Amount</p>
                  <p className="text-2xl font-bold">
                    {formatCurrency(result.summary.totalAmount)}
                  </p>
                </div>
              </div>

              <div className="mt-4 pt-4 border-t">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">Total Wimax Income</p>
                    <p className="text-2xl font-bold">
                      {formatCurrency(incomeStats.totalWimaxIncome)}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">Total ORPUS Income</p>
                    <p className="text-2xl font-bold">
                      {formatCurrency(incomeStats.totalOrpusIncome)}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">Total ORDA Income</p>
                    <p className="text-2xl font-bold">
                      {formatCurrency(incomeStats.totalOrdaIncome)}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">Total ORLOK Income</p>
                    <p className="text-2xl font-bold">
                      {formatCurrency(incomeStats.totalOrlokIncome)}
                    </p>
                  </div>
                  <div className="flex items-center justify-end">
                    <Button onClick={handleDownload} className="gap-2">
                      <Download className="h-4 w-4" />
                      Download Transfer Plan
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {result.unmatchedOrders.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertCircle className="h-5 w-5 text-orange-600" />
                  Unmatched Orders
                </CardTitle>
                <CardDescription>
                  {result.unmatchedOrders.length} orders could not be matched with payment requests
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {result.unmatchedOrders.slice(0, 20).map((order, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-2 rounded-md bg-muted/50"
                    >
                      <div className="flex-1">
                        <p className="text-sm font-medium font-mono">{order.order}</p>
                        <p className="text-xs text-muted-foreground">{order.customerEmail}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium">
                          {formatCurrency(order.amount)}
                        </p>
                        <p className="text-xs text-muted-foreground">{order.settlementTime}</p>
                      </div>
                    </div>
                  ))}
                  {result.unmatchedOrders.length > 20 && (
                    <p className="text-sm text-muted-foreground text-center pt-2">
                      ... and {result.unmatchedOrders.length - 20} more unmatched orders
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold">Confirm Transaction</h3>
                  <p className="text-sm text-muted-foreground">
                    {isConfirmed
                      ? "Transfer plan has been confirmed and payment requests have been updated."
                      : "Review the transfer plan above, then confirm to update payment requests with batch and income data."}
                  </p>
                </div>
                <Button
                  onClick={handleConfirm}
                  disabled={isConfirming || isConfirmed}
                  className="gap-2"
                >
                  {isConfirmed ? (
                    <>
                      <CheckCircle className="h-4 w-4" />
                      Confirmed
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="h-4 w-4" />
                      {isConfirming ? "Confirming..." : "Confirm Transaction"}
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>

          <TransferPlanTable result={result} loading={isProcessing} />
        </>
      )}
    </div>
  );
}

